import type { Trip } from '../types/trip';

/**
 * Lo que el modal entrega al confirmar. `at` ausente = «ahora» (lo pone el
 * servidor). Lo demás solo tiene sentido en un viaje con tarifa (FE-34):
 * `quantity` es el peso del manifiesto ya en formato canónico, `notes` lleva
 * la marca de «peso provisional», y `manifiesto` es la foto, que NO viaja en
 * el `start`: cuelga del ingreso que el `start` devuelve (ADR-032 §2.4), así
 * que la sube quien llama, después.
 */
export interface MomentoViaje {
  at?: string;
  quantity?: string;
  reference?: string;
  notes?: string;
  manifiesto?: File | null;
}

export const NOTA_PESO_PROVISIONAL = 'Peso provisional declarado al iniciar: pendiente de confirmar con el tiquete.';

/**
 * La secuencia completa de un momento del viaje, compartida por las páginas
 * que montan el modal (Viajes y Mi Desempeño). Devuelve un aviso NO bloqueante
 * cuando el viaje quedó en el estado nuevo pero la foto no llegó; lanza
 * cuando el propio cambio de estado falló (el modal sigue abierto).
 *
 * - **Iniciar**: primero el `start` (que crea el ingreso); si había foto, se
 *   sube DESPUÉS al ingreso devuelto. Si esa subida falla, el viaje ya está en
 *   curso —es lo correcto, ADR-032 §3— y se avisa para que la agreguen desde
 *   la tarjeta antes de finalizar.
 * - **Finalizar**: primero la foto (si el modal la pidió), luego el `finish`.
 *   Si la foto falla, NO se finaliza: la obligación es de la UI y esta es la
 *   única puerta.
 */
export async function ejecutarMomento(
  trip: Trip,
  accion: 'iniciar' | 'finalizar',
  datos: MomentoViaje,
  api: {
    iniciar: (tripId: string, data: { at?: string; quantity?: string; reference?: string; notes?: string }) => Promise<Trip>;
    finalizar: (tripId: string, at?: string) => Promise<Trip>;
    adjuntarManifiesto: (trip: Trip, file: File) => Promise<Trip>;
  },
): Promise<{ aviso?: string }> {
  if (accion === 'iniciar') {
    const iniciado = await api.iniciar(trip.id, { at: datos.at, quantity: datos.quantity, reference: datos.reference, notes: datos.notes });
    if (datos.manifiesto && iniciado.revenueId) {
      try {
        await api.adjuntarManifiesto(iniciado, datos.manifiesto);
      } catch {
        return { aviso: 'El viaje quedó en curso, pero la foto del manifiesto no se subió. Agrégala desde la tarjeta del viaje antes de finalizarlo.' };
      }
    }
    return {};
  }
  if (datos.manifiesto) await api.adjuntarManifiesto(trip, datos.manifiesto);
  await api.finalizar(trip.id, datos.at);
  return {};
}
